import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { articles, type ArticleData } from '@/mocks/articles';
import { mainSiteUrl } from '@/mocks/categories';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import JsonLd from '@/components/base/JsonLd';

function buildArticleJsonLd(article: ArticleData): Record<string, unknown> {
  const siteUrl = import.meta.env.VITE_SITE_URL;
  const canonicalUrl = `${siteUrl}/article/${article.slug}`;
  const faviconUrl = 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/1fc56da0b352904925ac780066826afe.jpeg';

  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: article.title,
    description: article.seoDescription,
    image: article.imageUrl,
    datePublished: '2026-08-06',
    dateModified: '2026-08-06',
    author: {
      '@type': 'Organization',
      name: 'Sugargoo Sheet',
    },
    publisher: {
      '@type': 'Organization',
      name: 'Sugargoo Sheet',
      logo: {
        '@type': 'ImageObject',
        url: faviconUrl,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': canonicalUrl,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to="/" replace />;
  }

  const canonicalUrl = `${import.meta.env.VITE_SITE_URL}/article/${article.slug}`;
  const jsonLd = buildArticleJsonLd(article);

  return (
    <main className="min-h-screen bg-background-50">
      <title>{article.seoTitle}</title>
      <meta name="description" content={article.seoDescription} />
      <meta name="keywords" content={article.seoKeywords} />
      <meta name="robots" content="index, follow" />
      <link rel="canonical" href={canonicalUrl} />
      <meta property="og:title" content={article.seoTitle} />
      <meta property="og:description" content={article.seoDescription} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="article" />
      <meta property="og:image" content={article.imageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={article.seoTitle} />
      <meta name="twitter:description" content={article.seoDescription} />
      <meta name="twitter:image" content={article.imageUrl} />
      <JsonLd data={jsonLd} />
      <Navbar />
      <article className="max-w-3xl mx-auto px-4 md:px-6 py-12 md:py-16">
        <ArticleHeader article={article} />
        <ArticleContent article={article} />
        <ArticleFooterNav article={article} />
      </article>
      <Footer />
    </main>
  );
}

function ArticleHeader({ article }: { article: ArticleData }) {
  return (
    <header className="mb-10">
      <div className="flex items-center gap-2 text-sm text-foreground-400 mb-4">
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Home</Link>
        <i className="ri-arrow-right-s-line text-xs"></i>
        <Link to="/" className="hover:text-primary-600 transition-colors cursor-pointer">Shopping Guide</Link>
        <i className="ri-arrow-right-s-line text-xs"></i>
        <span className="text-foreground-600 truncate max-w-[200px]">{article.title}</span>
      </div>

      <span className="inline-block px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-semibold mb-4">
        {article.category}
      </span>

      <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
        {article.title}
      </h1>

      <p className="text-foreground-500 text-sm md:text-base mb-6 leading-relaxed">
        {article.excerpt}
      </p>

      <div className="flex flex-wrap items-center gap-4 text-xs text-foreground-400">
        <span className="flex items-center gap-1">
          <i className="ri-calendar-line"></i> Updated: August 2026
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-time-line"></i> {article.readTime}
        </span>
        <span className="flex items-center gap-1">
          <i className="ri-bookmark-line"></i> Sugargoo Spreadsheet
        </span>
      </div>
    </header>
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <div className="prose-custom">
      {article.sections.map((section, idx) => (
        <section key={idx} className="mb-10 pb-10 border-b border-background-200 last:border-b-0">
          <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-5">
            {section.heading}
          </h2>

          {section.type === 'text' && (
            <div className="text-foreground-600 leading-relaxed text-sm md:text-base">
              <p>{section.content}</p>
            </div>
          )}

          {section.type === 'tips' && (
            <div className="space-y-4">
              {section.content && (
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base">{section.content}</p>
              )}
              <ul className="space-y-3">
                {section.items?.map((item) => (
                  <li key={item.label} className="flex items-start gap-3">
                    <i className="ri-close-circle-line text-primary-500 mt-0.5 flex-shrink-0"></i>
                    <span className="text-foreground-600 text-sm md:text-base">
                      <strong className="text-foreground-900">{item.label}:</strong> {item.content}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {section.type === 'faq' && (
            <div className="space-y-3">
              {section.content && (
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base">{section.content}</p>
              )}
              {section.items?.map((faq) => (
                <details key={faq.label} className="bg-white rounded-xl border border-background-200 group">
                  <summary className="px-5 py-4 cursor-pointer font-medium text-foreground-800 text-sm flex items-center justify-between hover:text-foreground-950 transition-colors">
                    {faq.label}
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform"></i>
                  </summary>
                  <div className="px-5 pb-4 text-foreground-600 text-sm leading-relaxed">
                    {faq.content}
                  </div>
                </details>
              ))}
            </div>
          )}

          {section.type === 'table' && (
            <div>
              {section.content && (
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-4">{section.content}</p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b-2 border-background-200">
                      <th className="text-left py-3 px-4 text-foreground-700 font-semibold whitespace-nowrap">Feature</th>
                      {section.items?.[0]?.value && (
                        <th className="text-left py-3 px-4 text-foreground-700 font-semibold whitespace-nowrap">Detail</th>
                      )}
                      {section.items?.[0]?.content && (
                        <th className="text-left py-3 px-4 text-foreground-700 font-semibold whitespace-nowrap">Notes</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="text-foreground-600">
                    {section.items?.map((row) => (
                      <tr key={row.label} className="border-b border-background-100">
                        <td className="py-2.5 px-4 font-medium text-foreground-800">{row.label}</td>
                        {row.value && <td className="py-2.5 px-4">{row.value}</td>}
                        {row.content && <td className="py-2.5 px-4 text-xs md:text-sm">{row.content}</td>}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {section.type === 'cta' && (
            <div className="text-center">
              {section.content && (
                <p className="text-foreground-600 leading-relaxed text-sm md:text-base mb-6">{section.content}</p>
              )}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-white font-medium text-sm rounded-full hover:bg-foreground-800 transition-all cursor-pointer"
                >
                  <i className="ri-home-4-line"></i>
                  Back to Homepage
                </Link>
                <a
                  href={mainSiteUrl}
                  target="_blank"
                  rel="noopener nofollow"
                  className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer"
                >
                  <i className="ri-shopping-bag-3-line"></i>
                  Start Shopping Now
                </a>
              </div>
            </div>
          )}
        </section>
      ))}

      <section className="mb-10">
        <div className="bg-background-100 rounded-xl p-6 text-center">
          <h3 className="font-heading font-semibold text-foreground-900 text-lg mb-2">
            Explore More Resources
          </h3>
          <p className="text-foreground-500 text-sm mb-4">
            Browse our complete collection of Sugargoo spreadsheet articles and shopping guides.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-foreground-950 text-white font-medium text-sm rounded-full hover:bg-foreground-800 transition-all cursor-pointer"
            >
              <i className="ri-home-4-line"></i>
              Back to Homepage
            </Link>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="whitespace-nowrap inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-white font-medium text-sm rounded-full hover:bg-primary-600 transition-all cursor-pointer"
            >
              <i className="ri-external-link-line"></i>
              Visit Sugargoo Spreadsheet Store
            </a>
          </div>
        </div>

        <p className="text-xs text-foreground-400 italic text-center mt-6">
          Disclaimer: This website is an independent educational resource. We are not affiliated with Sugargoo. All trademarks belong to their respective owners.
        </p>
      </section>
    </div>
  );
}

function ArticleFooterNav({ article }: { article: ArticleData }) {
  const currentIndex = articles.findIndex((a) => a.slug === article.slug);
  const prevArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  if (!prevArticle && !nextArticle) return null;

  return (
    <nav className="border-t border-background-200 pt-8 mt-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {prevArticle ? (
          <Link
            to={`/article/${prevArticle.slug}`}
            className="group flex items-start gap-3 p-4 bg-white rounded-xl border border-background-200 hover:border-primary-300 transition-all cursor-pointer"
          >
            <i className="ri-arrow-left-line text-foreground-400 group-hover:text-primary-600 transition-colors mt-0.5"></i>
            <div className="min-w-0">
              <span className="text-xs text-foreground-400">Previous Article</span>
              <p className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors truncate">
                {prevArticle.title}
              </p>
            </div>
          </Link>
        ) : (
          <div></div>
        )}

        {nextArticle ? (
          <Link
            to={`/article/${nextArticle.slug}`}
            className="group flex items-start justify-end gap-3 p-4 bg-white rounded-xl border border-background-200 hover:border-primary-300 transition-all cursor-pointer text-right"
          >
            <div className="min-w-0">
              <span className="text-xs text-foreground-400">Next Article</span>
              <p className="text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors truncate">
                {nextArticle.title}
              </p>
            </div>
            <i className="ri-arrow-right-line text-foreground-400 group-hover:text-primary-600 transition-colors mt-0.5"></i>
          </Link>
        ) : (
          <div></div>
        )}
      </div>
    </nav>
  );
}